import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  deleteTranslation,
  entityTypeLabel,
  fieldNameLabel,
  getTenantDefaultLanguage,
  languageLabel,
  listTranslations,
  setTenantDefaultLanguage,
  SUPPORTED_LANGUAGES,
  TranslationEntityType,
  TranslationRow,
  TranslationStatus,
  updateTranslation,
} from '../../services/translationsAdminService';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';

/**
 * Phase 2 (feat-018-learn-i18n) translation review queue.
 *
 * Managers/corporate drain the `fresh` Spanish queue: approve, inline-edit, or
 * reject auto-translated Learn instructional copy, side-by-side against the
 * canonical English. Mounted behind StaffRoute (ADR 0004 D5).
 *
 * SECURITY (ADR 0004 D4 / C5b): `translated_text` and `canonical_text` are
 * UNTRUSTED. They are rendered ONLY as `{value}` JSX text nodes (React escapes
 * by default) and bound to a controlled <textarea> for editing. There is NO
 * dangerouslySetInnerHTML / innerHTML / markdown-to-HTML anywhere in this file.
 */

const ENTITY_TYPE_OPTIONS: TranslationEntityType[] = [
  'learn_lesson',
  'learn_drill',
  'question_item',
  'rehearsal_template',
  'rehearsal_criterion',
  'trivia_set',
  'trivia_question',
];

interface EntityGroup {
  key: string;
  entityType: TranslationEntityType;
  entityId: string;
  rows: TranslationRow[];
}

/** Stable group key for one canonical entity. */
function groupKey(r: TranslationRow): string {
  return `${r.entity_type}::${r.entity_id}`;
}

/** Group rows by (entity_type, entity_id), preserving the server's order. */
function groupByEntity(rows: TranslationRow[]): EntityGroup[] {
  const groups = new Map<string, EntityGroup>();
  for (const r of rows) {
    const key = groupKey(r);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        entityType: r.entity_type,
        entityId: r.entity_id,
        rows: [],
      };
      groups.set(key, g);
    }
    g.rows.push(r);
  }
  return Array.from(groups.values());
}

const StatusBadge: React.FC<{ status: TranslationStatus }> = ({ status }) => {
  const approved = status === 'approved';
  return (
    <span
      className={
        approved
          ? 'inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800'
          : 'inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800'
      }
    >
      {approved ? 'Approved' : 'Auto-translated'}
    </span>
  );
};

const FieldRow: React.FC<{
  row: TranslationRow;
  busy: boolean;
  onApprove: (row: TranslationRow, editedText: string) => void;
  onReject: (row: TranslationRow) => void;
}> = ({ row, busy, onApprove, onReject }) => {
  const [draft, setDraft] = useState(row.translated_text);

  // Re-sync the draft when the underlying row changes identity/content (e.g.
  // after a reload), but not on every render.
  useEffect(() => {
    setDraft(row.translated_text);
  }, [row.id, row.translated_text]);

  const dirty = draft !== row.translated_text;

  return (
    <div
      className="border-t border-gray-100 py-3"
      data-testid={`translation-row-${row.id}`}
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wide text-inkMuted">
          {fieldNameLabel(row.field_name)}
        </span>
        <StatusBadge status={row.status} />
        {row.stale && (
          <span
            className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-800"
            data-testid={`translation-stale-${row.id}`}
            title="The English source changed after this translation was made — needs re-review."
          >
            Needs re-review
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Canonical English — read-only, untrusted plain text (C5b). */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
            English (canonical)
          </div>
          <div className="text-sm text-ink/90 whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded p-2 min-h-[2.5rem]">
            {row.canonical_text != null && row.canonical_text !== '' ? (
              row.canonical_text
            ) : (
              <span className="italic text-inkMuted">
                (English source missing — translation orphaned)
              </span>
            )}
          </div>
        </div>

        {/* Translation — editable controlled textarea, untrusted (C5b). */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-inkMuted mb-1">
            {languageLabel(row.language_code)} (translation)
          </div>
          <textarea
            className="w-full text-sm text-ink whitespace-pre-wrap border border-gray-300 rounded p-2 min-h-[2.5rem] focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy}
            rows={Math.max(2, draft.split('\n').length)}
            data-testid={`translation-edit-${row.id}`}
            aria-label={`${languageLabel(row.language_code)} translation for ${fieldNameLabel(
              row.field_name
            )}`}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <button
          type="button"
          className="text-sm font-medium px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          disabled={busy || draft.trim() === ''}
          onClick={() => onApprove(row, draft)}
          data-testid={`translation-approve-${row.id}`}
        >
          {dirty ? 'Save & approve' : 'Approve'}
        </button>
        <button
          type="button"
          className="text-sm font-medium px-3 py-1 rounded border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          disabled={busy}
          onClick={() => onReject(row)}
          data-testid={`translation-reject-${row.id}`}
          title="Delete this translation — the English original will show instead."
        >
          Reject
        </button>
        {dirty && (
          <button
            type="button"
            className="text-xs text-inkMuted hover:text-ink underline"
            disabled={busy}
            onClick={() => setDraft(row.translated_text)}
          >
            Revert edit
          </button>
        )}
        {busy && <span className="text-xs text-inkMuted">Saving…</span>}
      </div>
    </div>
  );
};

const EntityGroupCard: React.FC<{
  group: EntityGroup;
  busyIds: Record<string, boolean>;
  onApprove: (row: TranslationRow, editedText: string) => void;
  onReject: (row: TranslationRow) => void;
}> = ({ group, busyIds, onApprove, onReject }) => {
  return (
    <div className="mb-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="px-4 py-2 border-b border-gray-100 bg-sky-50/60 rounded-t-lg">
        <span className="text-sm font-semibold text-ink">
          {entityTypeLabel(group.entityType)}
        </span>
        <span className="ml-2 text-xs text-inkMuted font-mono">
          {group.entityId}
        </span>
      </div>
      <div className="px-4">
        {group.rows.map((r) => (
          <FieldRow
            key={r.id}
            row={r}
            busy={!!busyIds[r.id]}
            onApprove={onApprove}
            onReject={onReject}
          />
        ))}
      </div>
    </div>
  );
};

const TranslationReviewQueue: React.FC = () => {
  const { selectedTenant, tenantName } = useTenant();

  const [rows, setRows] = useState<TranslationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Record<string, boolean>>({});

  // Filters. Default to the active rollout language (Spanish) and the
  // common reviewer task (the fresh queue) via the needs-re-review toggle.
  const [language, setLanguage] = useState<string>('es');
  const [statusFilter, setStatusFilter] = useState<TranslationStatus | ''>('');
  const [entityType, setEntityType] = useState<TranslationEntityType | ''>('');
  const [needsReviewOnly, setNeedsReviewOnly] = useState<boolean>(true);

  // Tenant default-language control. Initialised to 'en' (the column default,
  // safe fallback) then pre-populated from the persisted value on load — so a
  // reviewer never sees a language they didn't choose pre-selected and cannot
  // silently flip the default by clicking Save.
  const [defaultLang, setDefaultLang] = useState<string>('en');
  const [defaultLangNote, setDefaultLangNote] = useState<string | null>(null);
  const [savingDefaultLang, setSavingDefaultLang] = useState(false);

  const reload = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listTranslations(selectedTenant, {
        language: language || undefined,
        status: statusFilter || undefined,
        entityType: entityType || undefined,
      });
      setRows(data);
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, language, statusFilter, entityType]);

  useEffect(() => {
    if (selectedTenant) reload();
    else setRows([]);
  }, [selectedTenant, reload]);

  // Pre-populate the tenant default-language selector from the persisted value
  // so it reflects reality (not a hard-coded guess). Falls back to 'en' on error.
  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    getTenantDefaultLanguage(selectedTenant)
      .then((lang) => {
        if (!cancelled) setDefaultLang(lang);
      })
      .catch(() => {
        /* keep the 'en' fallback; the selector stays usable */
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  // "Needs re-review" = fresh OR stale (ADR 0004 D3). Applied client-side on
  // top of the server filters.
  const visibleRows = useMemo(
    () =>
      needsReviewOnly
        ? rows.filter((r) => r.status === 'fresh' || r.stale)
        : rows,
    [rows, needsReviewOnly]
  );

  const groups = useMemo(() => groupByEntity(visibleRows), [visibleRows]);

  const setBusy = (id: string, val: boolean) =>
    setBusyIds((prev) => ({ ...prev, [id]: val }));

  const handleApprove = useCallback(
    async (row: TranslationRow, editedText: string) => {
      setBusy(row.id, true);
      setError(null);
      try {
        const changed = editedText !== row.translated_text;
        const result = await updateTranslation(row.id, {
          status: 'approved',
          ...(changed ? { translated_text: editedText } : {}),
        });
        // Reflect the new state locally without a full reload.
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  status: result.new_status,
                  translated_text: changed ? editedText : r.translated_text,
                  reviewed_by: result.reviewed_by,
                }
              : r
          )
        );
      } catch (e) {
        setError(String(e instanceof Error ? e.message : e));
      } finally {
        setBusy(row.id, false);
      }
    },
    []
  );

  const handleReject = useCallback(async (row: TranslationRow) => {
    const ok = window.confirm(
      `Reject this ${languageLabel(row.language_code)} translation? ` +
        'It will be deleted and the English original will show instead.'
    );
    if (!ok) return;
    setBusy(row.id, true);
    setError(null);
    try {
      await deleteTranslation(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
    } catch (e) {
      setError(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(row.id, false);
    }
  }, []);

  const handleSaveDefaultLang = useCallback(async () => {
    if (!selectedTenant) return;
    setSavingDefaultLang(true);
    setDefaultLangNote(null);
    try {
      await setTenantDefaultLanguage(selectedTenant, defaultLang);
      setDefaultLangNote('Saved.');
    } catch (e) {
      setDefaultLangNote(
        `Could not save: ${String(e instanceof Error ? e.message : e)}`
      );
    } finally {
      setSavingDefaultLang(false);
    }
  }, [selectedTenant, defaultLang]);

  return (
    <div className="min-h-screen bg-cream" data-testid="translation-review-queue">
      <ScorecardNavigation />

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-semibold text-ink mb-1">
              Learn Module — Translations
            </h1>
            <p className="text-sm text-inkMuted">
              Review and approve translated Learn copy for{' '}
              <span className="font-semibold text-ink">{tenantName}</span>.
              Auto-translated copy shows to trainees immediately; approving drops
              the marker. Rejecting reverts that field to English.
            </p>
          </div>
          <Link
            to="/quiz-management"
            className="shrink-0 text-sm text-blue-600 hover:text-blue-800 hover:underline mt-2"
          >
            ← Edit content
          </Link>
        </div>

        {!selectedTenant && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            No tenant selected. Return to a scorecard page and pick a tenant first.
          </div>
        )}

        {selectedTenant && (
          <>
            {/* Tenant default-language control (ADR 0004 D2). */}
            <div className="mb-5 p-3 bg-white border border-gray-200 rounded-lg flex items-center gap-3 flex-wrap">
              <label
                className="text-sm font-medium text-ink"
                htmlFor="tenant-default-language"
              >
                Tenant default language
              </label>
              <select
                id="tenant-default-language"
                className="text-sm border border-gray-300 rounded px-2 py-1"
                value={defaultLang}
                onChange={(e) => setDefaultLang(e.target.value)}
                data-testid="tenant-default-language-select"
              >
                {SUPPORTED_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="text-sm font-medium px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={savingDefaultLang}
                onClick={handleSaveDefaultLang}
                data-testid="tenant-default-language-save"
              >
                {savingDefaultLang ? 'Saving…' : 'Save'}
              </button>
              {defaultLangNote && (
                <span className="text-xs text-inkMuted">{defaultLangNote}</span>
              )}
              <span className="text-xs text-inkMuted">
                Falls back to English when a learner has no preferred language.
              </span>
            </div>

            {/* Filters. */}
            <div className="mb-5 flex items-end gap-4 flex-wrap">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-inkMuted mb-1">
                  Language
                </label>
                <select
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  data-testid="filter-language"
                >
                  <option value="">All</option>
                  {SUPPORTED_LANGUAGES.filter((l) => l.code !== 'en').map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-inkMuted mb-1">
                  Status
                </label>
                <select
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value as TranslationStatus | '')
                  }
                  data-testid="filter-status"
                >
                  <option value="">All</option>
                  <option value="fresh">Auto-translated</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-inkMuted mb-1">
                  Content type
                </label>
                <select
                  className="text-sm border border-gray-300 rounded px-2 py-1"
                  value={entityType}
                  onChange={(e) =>
                    setEntityType(e.target.value as TranslationEntityType | '')
                  }
                  data-testid="filter-entity-type"
                >
                  <option value="">All</option>
                  {ENTITY_TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {entityTypeLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink mb-1">
                <input
                  type="checkbox"
                  checked={needsReviewOnly}
                  onChange={(e) => setNeedsReviewOnly(e.target.checked)}
                  data-testid="filter-needs-review"
                />
                Needs re-review only
              </label>
              <button
                type="button"
                className="text-sm font-medium px-3 py-1 rounded border border-gray-300 text-ink hover:bg-gray-50 mb-1"
                onClick={reload}
                disabled={loading}
                data-testid="refresh-queue"
              >
                Refresh
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                {error}
              </div>
            )}

            {loading && (
              <div className="p-6 text-sm text-inkMuted">Loading translations…</div>
            )}

            {!loading && groups.length === 0 && (
              <div className="text-sm text-inkMuted italic p-6 bg-white border border-gray-200 rounded-lg">
                {needsReviewOnly
                  ? 'Nothing needs re-review for these filters. 🎉'
                  : 'No translations match these filters yet.'}
              </div>
            )}

            {!loading &&
              groups.map((g) => (
                <EntityGroupCard
                  key={g.key}
                  group={g}
                  busyIds={busyIds}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default TranslationReviewQueue;
